import axios from 'axios';
import { getToken } from './storage';

// Acceso directo a la PWA de EPIs sin volver a iniciar sesión.
//
// Antes de abrir EPIs se pide a la API un código de un solo uso (60 s) y se
// añade como `?sso=`; EPIs lo canjea por la misma sesión. El token nunca va
// en la URL. Si algo falla, EPIs se abre igual y pide login: nunca peor que antes.
//
// Llamada aparte del cliente `api`: ese cierra la sesión ante un 401, y un
// acceso directo fallido no debe sacar al usuario de App Controllers.

const EPIS_ORIGINS = new Set(
  [__EPIS_URL__, ...(__EPIS_ALIASES__ ?? [])].flatMap(u => {
    try { return [new URL(u).origin]; } catch { return []; }
  }),
);

/** ¿La URL es de la PWA de EPIs de este entorno (dominio propio, www o host técnico)? */
export function isEpisUrl(url: string | undefined | null): boolean {
  if (!url || EPIS_ORIGINS.size === 0) return false;
  try { return EPIS_ORIGINS.has(new URL(url).origin); } catch { return false; }
}

function tokenUsable(token: string): boolean {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
    // El back no entrega tokens a menos de 2 min de caducar: ni lo pedimos.
    return !exp || exp * 1000 - Date.now() > 2 * 60 * 1000;
  } catch { return false; }
}

/** Devuelve la URL con `?sso=<código>`, o la original si no se pudo obtener. */
export async function withEpisSso(url: string): Promise<string> {
  const token = getToken();
  if (!isEpisUrl(url) || !token || !tokenUsable(token)) return url;
  try {
    const { data } = await axios.post<{ code: string }>(`${__API_BASE_URL__}api/SsoHandoff`, null, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 4000,
    });
    if (!data?.code) return url;
    const u = new URL(url);
    u.searchParams.set('sso', data.code);
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Abre EPIs con acceso directo.
 * - App instalada (standalone): se navega la ventana; el sistema la abre en el
 *   navegador (o en la PWA de EPIs si está instalada y captura el enlace).
 * - Navegador: se abre YA una pestaña en blanco (para no perder el gesto del
 *   usuario y que no la bloquee el antipopups) y se le pone la URL al llegar el código.
 */
export function openEpis(url: string, standalone: boolean): void {
  if (standalone) {
    void withEpisSso(url).then(u => { window.location.href = u; });
    return;
  }
  const w = window.open('about:blank', '_blank');
  if (!w) {
    void withEpisSso(url).then(u => { window.location.href = u; });
    return;
  }
  // La pestaña nueva no debe poder tocar esta ventana (equivale a noopener).
  try { w.opener = null; } catch { /* algunos navegadores no lo permiten */ }
  void withEpisSso(url).then(u => { w.location.href = u; });
}
